import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ProjectFilesContext } from "modules/proposals/providers/ProjectFilesContextProvider";
import { useContext, useEffect, useMemo, useState } from "react";
import { ProjectFile, ProjectFileType } from "generated/graphql";
import { useGetProjectFileLazy } from "hooks/projectFiles/useGetProjectFileLazy";
import {
  Card,
  CircularProgress,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import { sentenceCase } from "change-case";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import useDeleteProjectFile from "hooks/projectFiles/useDeleteProjectFile";
import MDButton from "components/MDButton";
import isImage from "utils/images/isImage";
import { formatBytes } from "helpers/turfHelper";
import AddFileOrFolder from "./AddFileOrFolder";
import {
  buildPreviewUrl,
  ProjectFileGalleryCard,
} from "./ProjectFileGalleryCard";

const openFile = (path?: string | null) => {
  if (!path) {
    return;
  }

  window.open(buildPreviewUrl(path), "_blank");
};

export default function FileDetails({
  fileView,
  folderId,
  enableSelectFile = false,
  allowMultiSelect = false,
  submitFn,
  mode = "browse",
}: {
  data: ProjectFile[];
  fileView?: boolean;
  folderId?: string | number | null;
  enableSelectFile?: boolean;
  allowMultiSelect?: boolean;
  submitFn?: (folderId: string | string[]) => void;
  mode?: "browse" | "folder-select" | "file-attach";
}) {
  const theme = useTheme();
  const { selectedItems, setSelectedItems, selectedFileIds, setSelectedFileIds } =
    useContext(ProjectFilesContext);
  const [getProjectFile, { loading, graphqlData: fileById, missing }] = useGetProjectFileLazy();
  const [deleteProjectFile] = useDeleteProjectFile();
  const [open, setOpen] = useState(false);
  const [filePreviewPath, setFilePreviewPath] = useState<string | null>(null);
  const [filePreviewId, setFilePreviewId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedItems && typeof selectedItems === "string") {
      getProjectFile({ id: selectedItems });
      setSelectedFileIds([]);
    }
  }, [getProjectFile, selectedItems, setSelectedFileIds]);

  useEffect(() => {
    if (fileView && folderId) {
      setSelectedItems(folderId as string);
    }
  }, [fileView, folderId, setSelectedItems]);

  const folderFiles = useMemo(() => {
    if (!fileById || fileById.type !== ProjectFileType.FOLDER) {
      return [];
    }

    return (fileById.children ?? []).filter((item) => item.type === ProjectFileType.FILE);
  }, [fileById]);

  const acceptFn = () => {
    deleteProjectFile({ variables: { id: filePreviewId || (selectedItems as string) } });
    setOpen(false);
    if (filePreviewId) {
      setFilePreviewId(null);
    } else {
      setSelectedItems(null);
    }
  };

  const toggleSelectedFile = (fileId: string) => {
    setSelectedFileIds((current) => {
      if (allowMultiSelect) {
        return current.includes(fileId)
          ? current.filter((currentId) => currentId !== fileId)
          : [...current, fileId];
      }

      return current.includes(fileId) ? [] : [fileId];
    });
  };

  const clearSelectedFiles = () => {
    setSelectedFileIds([]);
  };

  const submitSelectedFiles = () => {
    if (!submitFn || selectedFileIds.length === 0) {
      return;
    }

    submitFn(allowMultiSelect ? selectedFileIds : selectedFileIds[0]);
  };

  const FilePreview = () => (
    <>
      {fileById?.path || filePreviewPath ? (
        <>
          {isImage(filePreviewPath || fileById?.path || "") ? (
            <MDBox textAlign="center">
              <MDTypography fontSize={16} fontWeight="bold">
                PREVIEW
              </MDTypography>
              <MDBox
                component="img"
                src={buildPreviewUrl(filePreviewPath || fileById?.path)}
                alt={fileById?.name}
                width={{ xs: "100%" }}
                mt={1}
              />
            </MDBox>
          ) : (
            <MDBox textAlign="center">
              <MDButton
                onClick={() => window.open(buildPreviewUrl(filePreviewPath || fileById?.path), "_blank")}
              >
                Download to preview
              </MDButton>
            </MDBox>
          )}
        </>
      ) : null}
    </>
  );

  const deleteModalConfirmationText = useMemo(() => {
    if (!fileById) return "";
    if (filePreviewId) {
      return `Delete ${fileById?.children?.find((c) => c.id === filePreviewId)?.name} File `;
    }
    return `Delete ${fileById.name} ${sentenceCase(ProjectFileType[fileById.type])}? ${
      fileById.children?.length > 0 ? "All subfiles and subfolders will also be deleted" : ""
    }`;
  }, [filePreviewId, fileById]);

  const renderSelectableFileGrid = () => (
    <MDBox
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: "white",
        borderRadius: "0 0 0.5rem 0.5rem",
        borderTop: "1px solid #e6e9ef",
      }}
    >
      <MDBox
        px={2}
        py={1.5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "#fff",
          borderBottom: "1px solid #eef1f5",
          boxShadow: "0 12px 24px rgba(15, 23, 42, 0.08)",
        }}
      >
        <MDTypography variant="button" fontWeight="medium">
          {selectedFileIds.length} selected
        </MDTypography>
        <MDBox
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            bgcolor: "#fff",
            borderRadius: "999px",
            px: 1,
            py: 0.5,
            boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
            border: "1px solid #eef1f5",
          }}
        >
          <MDButton
            variant="text"
            color="secondary"
            size="small"
            disabled={selectedFileIds.length === 0}
            onClick={clearSelectedFiles}
          >
            Clear
          </MDButton>
          <MDButton
            variant="contained"
            color="success"
            size="small"
            disabled={selectedFileIds.length === 0}
            onClick={submitSelectedFiles}
            sx={{ whiteSpace: "nowrap", minWidth: "124px" }}
          >
            {mode === "file-attach" ? "Attach selected" : "Add selected"}
          </MDButton>
        </MDBox>
      </MDBox>

      {folderFiles.length === 0 ? (
        <MDBox px={3} py={5} textAlign="center">
          <MDTypography variant="button" color="text">
            No files in this folder yet.
          </MDTypography>
        </MDBox>
      ) : (
        <MDBox
          p={2}
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
            gap: 2,
          }}
        >
          {folderFiles.map((file) => {
            const isSelected = selectedFileIds.includes(file.id);
            return (
              <MDBox
                key={file.id}
                onClick={() => toggleSelectedFile(file.id)}
                sx={{ cursor: "pointer" }}
              >
                <ProjectFileGalleryCard
                  file={file}
                  selected={isSelected}
                  selectable
                  size="100%"
                  footerMeta={formatBytes(file.fileSizeBytes)}
                />
              </MDBox>
            );
          })}
        </MDBox>
      )}
    </MDBox>
  );

  const renderBrowseFileGrid = () => (
    <MDBox
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: "white",
        borderRadius: "0 0 0.5rem 0.5rem",
        borderTop: "1px solid #e6e9ef",
      }}
    >
      {loading && (
        <MDBox px={3} py={4} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      {!loading && missing && (
        <MDBox px={3} py={5} textAlign="center">
          <MDTypography variant="button" color="text">
            This file or folder is no longer available.
          </MDTypography>
        </MDBox>
      )}

      {!loading && !missing && fileById?.type === ProjectFileType.FILE && (
        <MDBox p={2.5}>
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
              gap: 2,
            }}
          >
            {[fileById].map((file) => {
            return (
              <MDBox
                key={file.id}
                sx={{}}
              >
                <ProjectFileGalleryCard
                  file={file}
                  size="100%"
                  footerMeta={formatBytes(file.fileSizeBytes)}
                  onPrimaryClick={() => openFile(file.path)}
                  onDelete={() => {
                    setFilePreviewId(file.id);
                    setOpen(true);
                  }}
                />
              </MDBox>
            );
          })}
          </MDBox>
        </MDBox>
      )}

      {!loading && !missing && fileById?.type === ProjectFileType.FOLDER && folderFiles.length === 0 && (
        <MDBox px={3} py={5} textAlign="center">
          <MDTypography variant="button" color="text">
            No files in this folder yet.
          </MDTypography>
        </MDBox>
      )}

      {!loading && !missing && fileById?.type === ProjectFileType.FOLDER && folderFiles.length > 0 && (
        <MDBox
          p={2}
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
            gap: 2,
          }}
        >
          {folderFiles.map((file) => {
            return (
              <MDBox
                key={file.id}
                sx={{}}
              >
                <ProjectFileGalleryCard
                  file={file}
                  size="100%"
                  footerMeta={formatBytes(file.fileSizeBytes)}
                  onPrimaryClick={() => openFile(file.path)}
                  onDelete={() => {
                    setFilePreviewId(file.id);
                    setOpen(true);
                  }}
                />
              </MDBox>
            );
          })}
        </MDBox>
      )}
    </MDBox>
  );

  return (
    <MDBox display="flex" flexDirection="column" sx={{ height: "100%", minHeight: 0 }}>
      <Card
        sx={{
          maxWidth: "100%",
          boxShadow: "none",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          flexShrink: 0,
        }}
      >
        <AddFileOrFolder
          fileView={fileView}
          loading={loading}
          lastSelectedItem={selectedItems as string}
          fileById={fileById}
          onFolderCreated={(newFolderId) => {
            setSelectedItems(newFolderId);
          }}
          showAddFolder={false}
        />
      </Card>
      {selectedItems && (
        <>
          {enableSelectFile ? (
            renderSelectableFileGrid()
          ) : (
            renderBrowseFileGrid()
          )}
          {fileById && !enableSelectFile && (
            <ActionConfirmDialog
              open={open}
              setOpen={setOpen}
              handleAccept={acceptFn}
              handleCancel={() => setOpen(false)}
              title="Confirmation"
              content={deleteModalConfirmationText}
            />
          )}
        </>
      )}
    </MDBox>
  );
}
